import React from "react";
import {
  YStack,
  XStack,
  Image,
  SizableText,
  H3,
  H4,
  Card,
  Separator,
  Button,
  Spinner
} from "tamagui";
import { useQuery } from "@tanstack/react-query";
import { MaterialCommunityIcons, MaterialIcons } from "@expo/vector-icons";
import * as Animatable from 'react-native-animatable';
import { View, Dimensions } from "react-native";
import ErrorBoundary from "@utils/ErrorBoundary";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import apis from "@api/api";

const safeText = (value: any) => (typeof value === "string" && value ? value : "N/A");

const SlackOptions = () => {
  const theme = themeSelector();
  const screenWidth = Dimensions.get('window').width;
  const isTablet = screenWidth > 768;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["slackProfile"],
    queryFn: async () => await apis.getSlackProfile(),
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });

  const profile = data?.profile || {};
  const profileImageUri =
    profile.image_1024 ||
    profile.image_512 ||
    profile.image_192 ||
    profile.image_72;

  return (
    <ErrorBoundary>
      <YStack gap="$4">
        {/* Error State */}
        {error && (
          <Animatable.View animation="fadeInUp" duration={600}>
            <Card
              backgroundColor={theme ? "#fef2f2" : "#450a0a"}
              borderColor={theme ? "#fecaca" : "#7f1d1d"}
              borderRadius={16}
              padding="$5"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <View style={{
                  backgroundColor: theme ? "#fee2e2" : "#7f1d1d",
                  borderRadius: 50,
                  padding: 16
                }}>
                  <MaterialIcons name="error-outline" size={48} color="#ef4444" />
                </View>
                <H3 color="#ef4444" fontWeight="600" textAlign="center">
                  Failed to load Slack profile
                </H3>
                <SizableText
                  size="$4"
                  color={theme ? "#64748b" : "#94a3b8"}
                  textAlign="center"
                >
                  Please try reconnecting your Slack workspace, or try removing and re-adding the integration.  
                </SizableText>
                <Button
                  backgroundColor="#ef4444"
                  color="white"
                  onPress={() => refetch()}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="refresh" size={20} color="white" />
                    <SizableText color="white" fontWeight="600">
                      Retry Connection
                    </SizableText>
                  </XStack>
                </Button>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Loading State */}
        {isLoading && (
          <Animatable.View animation="fadeIn" duration={600}>
            <Card
              backgroundColor={theme ? "#f8fafc" : "#1e293b"}
              borderColor={theme ? "#e2e8f0" : "#334155"}
              borderRadius={16}
              padding="$6"
              elevate
            >
              <YStack alignItems="center" gap="$4">
                <Spinner size="large" color="#4A154B" />
                <H4 color={theme ? "#1e293b" : "#f1f5f9"} fontWeight="600">
                  Loading Slack profile...
                </H4>
              </YStack>
            </Card>
          </Animatable.View>
        )}

        {/* Profile Data */}
        {profile && Object.keys(profile).length > 0 && (
          <>
            <Animatable.View animation="fadeInUp" duration={600} delay={100}>
              <Card
                backgroundColor={theme ? "#ffffff" : "#1e293b"}
                borderColor={theme ? "#e2e8f0" : "#334155"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                {/* Header with Profile Info */}
                <XStack alignItems="center" gap="$4" marginBottom="$4">
                  {profileImageUri ? (
                    <View style={{
                      width: 70,
                      height: 70,
                      borderRadius: 35,
                      overflow: 'hidden',
                      backgroundColor: '#4A154B'
                    }}>
                      <Image
                        source={{ uri: profileImageUri }}
                        width={70}
                        height={70}
                      />
                    </View>
                  ) : (
                    <View style={{
                      width: 70,
                      height: 70,
                      borderRadius: 35,
                      backgroundColor: '#4A154B',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <SizableText color="white" fontSize="$9" fontWeight="bold">
                        {profile.real_name?.charAt(0)?.toUpperCase() || 'S'}
                      </SizableText>
                    </View>
                  )}

                  <YStack flex={1}>
                    <H4
                      color={theme ? "#1e293b" : "#f1f5f9"}
                      fontWeight="700"
                      numberOfLines={1}
                    >
                      {safeText(profile.real_name)}
                    </H4>
                    <SizableText
                      size="$4"
                      color={theme ? "#64748b" : "#94a3b8"}
                      numberOfLines={1}
                    >
                      {safeText(profile.title || profile.status_text) || "No title provided"}
                    </SizableText>
                  </YStack>
                </XStack>

                <Separator borderColor={theme ? "#e2e8f0" : "#334155"} marginBottom="$4" />

                {/* Profile Details Grid */}
                <YStack gap="$3">
                  {profile.email && profile.email !== "N/A" && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#faf5ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="email" size={20} color="#4A154B" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Email
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                          numberOfLines={1}
                        >
                          {profile.email}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {profile.phone && profile.phone !== "N/A" && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#faf5ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialIcons name="phone" size={20} color="#4A154B" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Phone
                        </SizableText>
                        <SizableText
                          size="$4"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                        >
                          {profile.phone}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {profile.bot_id && profile.bot_id !== "N/A" && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#faf5ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialCommunityIcons name="robot" size={20} color="#4A154B" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Bot ID
                        </SizableText>
                        <SizableText
                          size="$3"
                          color={theme ? "#1e293b" : "#f1f5f9"}
                          fontWeight="600"
                          numberOfLines={1}
                        >
                          {profile.bot_id}
                        </SizableText>
                      </YStack>
                    </XStack>
                  )}

                  {profile.status_text && profile.status_text !== "N/A" && (
                    <XStack alignItems="center" gap="$3">
                      <View style={{
                        backgroundColor: theme ? "#faf5ff" : "#581c87",
                        borderRadius: 8,
                        padding: 8
                      }}>
                        <MaterialCommunityIcons name="message-text" size={20} color="#4A154B" />
                      </View>
                      <YStack flex={1}>
                        <SizableText
                          size="$2"
                          color={theme ? "#64748b" : "#94a3b8"}
                        >
                          Status
                        </SizableText>
                        <XStack alignItems="center" gap="$2">
                          {profile.status_emoji && (
                            <SizableText size="$5">{profile.status_emoji}</SizableText>
                          )}
                          <SizableText
                            size="$4"
                            color={theme ? "#1e293b" : "#f1f5f9"}
                            fontWeight="600"
                            flex={1}
                          >
                            {profile.status_text}
                          </SizableText>
                        </XStack>
                      </YStack>
                    </XStack>
                  )}
                </YStack>
              </Card>
            </Animatable.View>

            {/* Success Message */}
            <Animatable.View animation="fadeInUp" duration={600} delay={200}>
              <Card
                backgroundColor={theme ? "#f0fdf4" : "#064e3b"}
                borderColor={theme ? "#dcfce7" : "#065f46"}
                borderRadius={16}
                padding="$5"
                elevate
              >
                <YStack alignItems="center" gap="$3">
                  <View style={{
                    backgroundColor: theme ? "#d1fae5" : "#065f46",
                    borderRadius: 50,
                    padding: 12
                  }}>
                    <MaterialIcons name="check-circle" size={32} color={theme ? "#10b981" : "#34d399"} />
                  </View>
                  <H4
                    color={theme ? "#059669" : "#10b981"}
                    fontWeight="600"
                    textAlign="center"
                  >
                    Slack Connected Successfully!
                  </H4>
                  <SizableText
                    size="$4"
                    color={theme ? "#065f46" : "#34d399"}
                    textAlign="center"
                  >
                    You can now access your Slack workspace and manage communications directly from SmartSaaS.
                  </SizableText>
                </YStack>
              </Card>
            </Animatable.View>
          </>
        )}
      </YStack>
    </ErrorBoundary>
  );
};

export default SlackOptions;
